import { useEffect, useState } from 'react'
import { DataLayer } from '../lib/dataLayer'
import { Database } from '../types/database'

type CardReviewHistory = Database['public']['Tables']['card_review_history']['Row']

interface MasteryVisualizationProps {
  userId: string
  cardId: string
  currentMastery: number
}

export function MasteryVisualization({ userId, cardId, currentMastery }: MasteryVisualizationProps) {
  const [reviewHistory, setReviewHistory] = useState<CardReviewHistory[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchHistory = async () => {
      setLoading(true)
      try {
        const history = await DataLayer.getReviewHistory(userId, cardId, 20)
        setReviewHistory(history)
      } catch (error) {
        console.error('Failed to fetch review history:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchHistory()
  }, [userId, cardId])

  // Reverse history to show oldest first
  const displayHistory = [...reviewHistory].reverse()

  return (
    <div className="mb-4">
      {/* Review History Dots */}
      <div className="flex items-center gap-1 flex-wrap">
        {loading ? (
          <div className="text-xs text-gray-400">Loading history...</div>
        ) : displayHistory.length === 0 ? (
          <div className="text-xs text-gray-400">No review history yet</div>
        ) : (
          displayHistory.map((review, index) => {
            const isRecalled = review.result === 'recalled' || review.result === 'mastered'
            const isMissed = review.result === 'missed'
            
            // Calculate opacity for green dots based on mastery score after
            const greenOpacity = isRecalled 
              ? 0.3 + (review.mastery_score_after / 100) * 0.7 
              : 1

            return (
              <div
                key={review.id}
                className={`
                  w-2 h-2 rounded-full transition-all duration-200
                  ${isRecalled 
                    ? 'bg-green-500' 
                    : isMissed 
                      ? 'bg-gray-400' 
                      : 'bg-yellow-500'
                  }
                `}
                style={{
                  opacity: isRecalled ? greenOpacity : 1,
                  boxShadow: review.result === 'mastered' ? '0 0 4px rgba(34, 197, 94, 0.5)' : 'none'
                }}
                title={`${review.result} - ${review.mastery_score_after}% (${new Date(review.reviewed_at).toLocaleDateString()})`}
              />
            )
          })
        )}
        
        {/* Show ellipsis if there are more reviews */}
        {!loading && reviewHistory.length >= 20 && (
          <span className="text-xs text-gray-400 ml-1">...</span>
        )}
      </div>
    </div>
  )
}